import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getFridaysLeftInYear } from '../../utils/helpers';
import './index.scss';

const TipCard = ({ 
  donationType,
  donationAmount, 
  onTipSubmit 
}) => {
  const [sliderValue, setSliderValue] = useState(10); // Default percentage
  const [customAmount, setCustomAmount] = useState('');
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const { t } = useTranslation();
  const fridaysLeft = getFridaysLeftInYear(); // Get the number of Fridays left in the year

  // Submit initial tip value on component mount
  useEffect(() => {
    submitTipValue();
  }, [sliderValue, donationAmount]);

  // Handle slider changes
  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setSliderValue(value);
    setIsCustomAmount(false);
  };

  // Calculate and submit the tip amount
  const submitTipValue = () => {
    if (isCustomAmount && customAmount) {
      const numericValue = parseInt(customAmount, 10);
      onTipSubmit(numericValue);
    } else {
      const percentage = sliderValue / 100;
      onTipSubmit(Math.round(donationAmount * percentage));
    }
  };

  // Handle custom amount input
  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setCustomAmount(value);
  };

  const handleCustomAmountBlur = () => {
    if (customAmount) {
      submitTipValue();
    }
  };

  // Toggle between slider and custom input
  const toggleCustomAmount = () => {
    if (isCustomAmount) {
      // Switching back to slider
      setIsCustomAmount(false);
      submitTipValue();
    } else {
      // Switching to custom input
      setIsCustomAmount(true);
      setCustomAmount('');
    }
  };

  // Slider configuration
  const sliderConfig = { min: 5, max: 20, step: 1 };

  // Get slider output display text
  const getSliderOutputText = () => {
    return `${sliderValue}% (£${Math.round(donationAmount * (sliderValue / 100))})`;
  };

  return (
    <div className="tip-card">
      <div className="tip-card-header">
        <h3 className="text-xl font-semibold mb-3">
          {t('supportTechTeam')}
        </h3>
      </div>
      
      <div className="tip-card-body">
        <p className="tip-card-description">
          {t('supportTechTeamDescription')}
        </p>
        
        {!isCustomAmount ? (
          <div className="slider-container">
            <div className="slider-header">
              <span>{t('minPercentage')}</span>
              <span className="slider-value">{getSliderOutputText()}</span>
              <span>{t('maxPercentage')}</span>
            </div>
            
            <input 
              type="range"
              min={sliderConfig.min}
              max={sliderConfig.max}
              step={sliderConfig.step}
              value={sliderValue}
              onChange={handleSliderChange}
              className="tip-slider"
            />
            
            <div className="slider-footer">
              <span>{sliderConfig.min}%</span>
              <span>{sliderConfig.max}%</span>
            </div>
          </div>
        ) : (
          <div className="custom-input-container">
            <div className="custom-input">
              <span>£</span>
              <input
                type="text"
                value={customAmount}
                onChange={handleCustomAmountChange}
                onBlur={handleCustomAmountBlur}
                placeholder={t('enterAmount')}
                className="custom-amount-input"
                autoFocus
              />
            </div>
          </div>
        )}
        
        <span 
          onClick={toggleCustomAmount}
          className="toggle-input-button"
        >
          {isCustomAmount 
            ? t('useSlider')
            : t('autoAjr.enterCustomAmount')}
        </span>
      </div>
      {showTipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md relative">
            <span 
              type="button"
              className="absolute top-3 right-3"
              onClick={() => setShowTipModal(false)}
            >
              <X className="w-6 h-6" />
            </span>
            <div className="text-center text-green-500 text-4xl mb-3">🌱</div>
            <h3 className="text-xl font-bold text-center mb-3">{t('autoAjr.whatsThisFee')}</h3>
            <p className="mb-5">
                {t('autoAjr.tipDescriptionRecurringDetailed')}
            </p>
            <p className="mb-5">
              {t('autoAjr.weeklyDonationDisclaimer', { fridaysLeft })}
            </p>
            <button 
              type="button"
              className="w-full py-3 bg-blue-900 text-white font-bold rounded-lg"
              onClick={() => setShowTipModal(false)}
            >
              {t('autoAjr.gotIt')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

TipCard.propTypes = {
  donationAmount: PropTypes.number.isRequired,
  onTipSubmit: PropTypes.func.isRequired
};

export default TipCard;
